// ABOUTME: Renders a single shareable link row with program info, status, and copy action.
// ABOUTME: Displays title, program type, partner logo (or name fallback), shareable link status, and copy button.
import { when } from '@execonline-inc/maybe-adapter';
import { AlreadyTranslated } from '@execonline-inc/translations';
import clsx from 'clsx';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { assertNever } from '@kofno/piper';
import { OfferingType } from '../../../../../../../Types';
import { ShareableLink } from '../../../../Types';
import CopyButton from './CopyButton';
import ShareUrlStore from './ShareUrlStore';
import ShareUrlReactions from './ShareUrlStore/Reactions';
import ShareableLinkStatus from './ShareableLinkStatus';
import {
  T,
  TPlainTextKey,
  TranslationsContext,
  translation,
} from '../../../../../../../../../Translations';
import Image from '../../../../../../../../Image';

interface Props {
  programFamilyShareUrl: ShareableLink;
}

const courseTypeLabel = (offeringType: OfferingType): TPlainTextKey => {
  switch (offeringType) {
    case 'aep':
      return 'On-Demand Course';
    case 'epc':
      return 'Professor-Led Course';
    case 'coaching':
      return 'Leadership Coaching';
    case 'group-coaching':
      return 'Group Coaching';
    case 'program-sequence':
      return 'Program Sequence';
    default:
      return assertNever(offeringType);
  }
};

function whenShareUrlIsActiveIn(store: ShareUrlStore): Maybe<{}> {
  return when(store.state.shareUrlResource.payload.active, {});
}

function handleToggle(store: ShareUrlStore) {
  return whenShareUrlIsActiveIn(store)
    .do(() => store.deactivating())
    .elseDo(() => store.activating());
}

function Body({ programFamilyShareUrl }: Props) {
  const store = useMemo(
    () => new ShareUrlStore(programFamilyShareUrl.shareUrl),
    [programFamilyShareUrl.shareUrl],
  );
  const isActive = whenShareUrlIsActiveIn(store).isJust();
  const { links } = store.state.shareUrlResource;
  return (
    <>
      <tr
        className={clsx('bg-card border-b-1 border-border text-sm', {
          'text-muted-foreground': !isActive,
        })}
      >
        <td
          className={clsx('p-5 text-left font-bold w-2/6', {
            'text-primary': isActive,
            'text-muted-foreground': !isActive,
          })}
        >
          <AlreadyTranslated content={programFamilyShareUrl.name} />
        </td>
        <td className={'p-5'}>
          <T kind={courseTypeLabel(programFamilyShareUrl.experience.payload.offeringType)} />
        </td>
        <td className={'p-5'}>
          {programFamilyShareUrl.experience.payload.schoolPartnerLogoUrl
            .map((url) => (
              <TranslationsContext.Consumer key="logo">
                {(ts) => (
                  <Image
                    src={url}
                    alt={translation('University partner logo')(ts)}
                    style={'max-h-11'}
                  />
                )}
              </TranslationsContext.Consumer>
            ))
            .getOrElse(() =>
              programFamilyShareUrl.experience.payload.schoolPartnerName
                .map((name) => <>{name}</>)
                .getOrElse(() => <></>),
            )}
        </td>
        <td className="p-5">
          <ShareableLinkStatus
            isActive={isActive}
            onToggle={() => handleToggle(store)}
            programName={programFamilyShareUrl.name.text}
          />
        </td>
        <td className="p-5">
          <span
            className={clsx({
              'opacity-30 pointer-events-none': !isActive,
            })}
          >
            <CopyButton links={links} />
          </span>
        </td>
      </tr>
      <ShareUrlReactions store={store} fireImmediately />
    </>
  );
}

export default observer(Body);
